import { useEffect, useState } from 'react';

interface LoadableData<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
}

export function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

export function useArrayBuffer(url: string): LoadableData<ArrayBuffer> {
  const [state, setState] = useState<LoadableData<ArrayBuffer>>({
    data: undefined,
    error: undefined,
    loading: true,
  });

  useEffect(() => {
    const abortController = new AbortController();

    async function load() {
      setState({ data: undefined, error: undefined, loading: true });

      try {
        const response = await fetch(url, { cache: 'no-store', signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`File fetch failed with HTTP ${response.status}`);
        }

        const data = await response.arrayBuffer();
        setState({ data, error: undefined, loading: false });
      } catch (error) {
        if (abortController.signal.aborted) return;
        setState({ data: undefined, error: toError(error), loading: false });
      }
    }

    void load();

    return () => abortController.abort();
  }, [url]);

  return state;
}

export function useText(url: string): LoadableData<string> {
  const [state, setState] = useState<LoadableData<string>>({
    data: undefined,
    error: undefined,
    loading: true,
  });

  useEffect(() => {
    const abortController = new AbortController();

    async function load() {
      setState({ data: undefined, error: undefined, loading: true });

      try {
        const response = await fetch(url, { cache: 'no-store', signal: abortController.signal });
        if (!response.ok) {
          throw new Error(`File fetch failed with HTTP ${response.status}`);
        }

        const data = await response.text();
        setState({ data, error: undefined, loading: false });
      } catch (error) {
        if (abortController.signal.aborted) return;
        setState({ data: undefined, error: toError(error), loading: false });
      }
    }

    void load();

    return () => abortController.abort();
  }, [url]);

  return state;
}
